/**
 * AuthCallback - OIDC Callback Handler
 *
 * Handles the OAuth2/OIDC callback after user authentication:
 * 1. Extract authorization code and state from URL
 * 2. Delegate to AuthClient.handleCallback() for CSRF validation + PKCE exchange
 * 3. Redirect to original destination
 */

import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuthCallback } from '@agion/auth'
import { Button } from '@agion/ui'
import { LoadingSpinner } from '../shared/LoadingSpinner'

export function AuthCallback() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const onSuccess = useCallback((path: string) => navigate(path, { replace: true }), [navigate])
  const { status, restart } = useAuthCallback(onSuccess)

  if (status !== 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h1 className="text-xl font-semibold">{t('common:signInRecovery.title', { defaultValue: 'Sign-in interrupted' })}</h1>
          </div>
          <p role="alert" className="text-gray-600 dark:text-gray-300 mb-4">
            {status === 'restart_failed'
              ? t('common:signInRecovery.restartFailed', { defaultValue: 'We could not start sign-in. Check that your browser allows site storage, then try again.' })
              : t('common:signInRecovery.message', { defaultValue: 'We could not complete this sign-in. Please sign in again to continue.' })}
          </p>
          <Button onClick={() => { void restart() }} disabled={status === 'restarting'}>
            {status === 'restarting'
              ? t('common:signInRecovery.starting', { defaultValue: 'Starting sign-in…' })
              : t('common:signInRecovery.action', { defaultValue: 'Sign in again' })}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-600 dark:text-gray-300">{t('common:signInRecovery.processing', { defaultValue: 'Completing sign-in…' })}</p>
      </div>
    </div>
  )
}
